import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Loader2, Store } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { flatNavItems } from '@/components/layout/nav.config';
import { formatDate, formatTime, parseApiDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useUserPageHistory, type PageVisitEntryDto } from './page-visits.api';

/**
 * Where one account has been, newest first.
 *
 * The counterpart to the home page's frequently-visited grid: that one ranks,
 * this one lists. Support opens it to answer "what were you doing before it
 * broke" without asking the caller to retrace their own steps.
 *
 * One account has one history across every Gopanza app it uses, so the list is
 * ordered by when things happened and each row says which app it happened in.
 * The picker narrows to one app when the answer is only about that one.
 *
 * A visit is stored as a nav key rather than a URL, so labels come from the
 * navigation config here rather than from the API — a page that is renamed
 * reads by its new name all the way back through the history. That only works
 * for this app's own keys; another app's key is shown as it was sent, which is
 * why the domain is on the row and not assumed.
 */

/** What this app records under. Its keys are the ones we can label. */
const OWN_DOMAIN = 'admin-portal';

const WINDOWS = [7, 30, 90] as const;

const NAV_LABELS = new Map(
  flatNavItems.map((item) => [
    item.key,
    { label: item.label, parentLabel: item.parentLabel },
  ])
);

/**
 * The app's name if we have one for it, and the slug itself if we do not.
 *
 * The API takes any well-formed domain without being told about it first, so a
 * new client app shows up here the day it starts writing — reading as its slug
 * until somebody adds a name for it, rather than not appearing at all.
 */
function useDomainLabel() {
  const { t } = useTranslation();
  return (domain: string) =>
    t(`pageHistory.apps.${domain}`, { defaultValue: domain });
}

function DayHeading({ date }: { date: Date }) {
  const { t } = useTranslation();

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  const label = sameDay(date, today)
    ? t('pageHistory.today')
    : sameDay(date, yesterday)
      ? t('pageHistory.yesterday')
      : formatDate(date);

  return (
    <div className="bg-card/95 sticky top-0 z-10 py-1.5 backdrop-blur">
      <h4 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        {label}
      </h4>
    </div>
  );
}

function VisitRow({
  visit,
  showDomain,
}: {
  visit: PageVisitEntryDto;
  /** Off when the list is already pinned to one app, where it would only repeat. */
  showDomain: boolean;
}) {
  const { t } = useTranslation();
  const domainLabel = useDomainLabel();
  const known = visit.domain === OWN_DOMAIN ? NAV_LABELS.get(visit.page) : null;

  return (
    <li className="flex items-baseline gap-3 py-1.5">
      <span className="text-muted-foreground w-14 shrink-0 text-xs tabular-nums">
        {formatTime(visit.visitedOn)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-sm font-medium">
            {known ? t(known.label) : visit.page}
          </span>
          {known?.parentLabel && (
            <span className="text-muted-foreground text-xs">
              {t(known.parentLabel)}
            </span>
          )}
          {showDomain && (
            <Badge variant="outline" className="text-[10px] font-normal">
              {domainLabel(visit.domain)}
            </Badge>
          )}
        </span>
        {/* The key is what the row is about; the path is only there for the
            times it matters which record was open. */}
        {visit.path && (
          <span className="text-muted-foreground/70 block truncate font-mono text-[11px]">
            {visit.path}
          </span>
        )}
      </span>

      <span className="text-muted-foreground flex shrink-0 items-center gap-1 text-xs">
        {visit.businessName ? (
          <>
            <Store className="size-3" />
            <span className="max-w-32 truncate">{visit.businessName}</span>
          </>
        ) : (
          <>
            <Globe className="size-3" />
            <span>{t('pageHistory.global')}</span>
          </>
        )}
      </span>
    </li>
  );
}

/** Visits under the day they happened on, in the reader's own zone. */
function byDay(visits: PageVisitEntryDto[]) {
  const days: { key: string; date: Date; visits: PageVisitEntryDto[] }[] = [];

  for (const visit of visits) {
    const date = parseApiDate(visit.visitedOn);
    if (!date) continue;
    const key = date.toDateString();
    const last = days[days.length - 1];
    // The list is already newest first, so a day is only ever the one being
    // filled or a new one — no lookup, and no re-sorting of what the API sent.
    if (last?.key === key) {
      last.visits.push(visit);
    } else {
      days.push({ key, date, visits: [visit] });
    }
  }

  return days;
}

/**
 * The apps to offer in the picker.
 *
 * Nothing lists them: the API takes any well-formed domain on purpose, so the
 * set is whatever has actually been written. It is accumulated across the pages
 * loaded so far rather than recomputed from them, so narrowing to one app does
 * not drop the others out of the picker that got you there. An app this account
 * only used months ago appears once the reader pages back far enough — which is
 * the same point at which filtering by it would start to mean anything.
 */
function useObservedDomains(userId: string, visits: PageVisitEntryDto[]) {
  const [seen, setSeen] = useState<string[]>([]);

  useEffect(() => setSeen([]), [userId]);

  useEffect(() => {
    setSeen((current) => {
      const next = new Set(current);
      for (const visit of visits) next.add(visit.domain);
      return next.size === current.length ? current : [...next].sort();
    });
  }, [visits]);

  return seen;
}

export function VisitHistory({
  userId,
  windowDays,
  onWindowChange,
  domain,
  onDomainChange,
}: {
  userId: string;
  /** Trailing days, or null for everything still retained. */
  windowDays: number | null;
  onWindowChange: (days: number | null) => void;
  /** One client app, or null for all of them. */
  domain: string | null;
  onDomainChange: (domain: string | null) => void;
}) {
  const { t } = useTranslation();
  const domainLabel = useDomainLabel();
  const history = useUserPageHistory(userId, { windowDays, domain });

  const visits = useMemo(() => history.data?.pages.flat() ?? [], [history.data]);
  const days = useMemo(() => byDay(visits), [visits]);
  const observed = useObservedDomains(userId, visits);

  // The current filter is always offerable, even before a row proves it exists.
  const apps = useMemo(() => {
    const all = new Set(observed);
    if (domain) all.add(domain);
    return [...all].sort();
  }, [observed, domain]);

  // `isLoading`, not `isPending`: the query waits on a user id.
  const loading = history.isLoading;
  const empty = !loading && !history.isError && visits.length === 0;
  // A new range or app replacing the rows on screen. Load more has its own
  // spinner on its button, and keeps the rows it is adding to.
  const busy = history.isFetching && !loading && !history.isFetchingNextPage;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-xs">{t('pageHistory.help')}</p>

        <div className="flex flex-wrap items-center gap-2">
          {/* Offered only once there is a choice to make: an account that has
              only ever used one app does not need a picker with one option. */}
          {apps.length > 1 && (
            <Select
              value={domain ?? 'all'}
              onValueChange={(value) =>
                onDomainChange(value === 'all' ? null : value)
              }
              disabled={history.isFetching}
            >
              <SelectTrigger className="h-8 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('pageHistory.allApps')}</SelectItem>
                {apps.map((app) => (
                  <SelectItem key={app} value={app}>
                    {domainLabel(app)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select
            value={windowDays === null ? 'all' : String(windowDays)}
            onValueChange={(value) =>
              onWindowChange(value === 'all' ? null : Number(value))
            }
            // A range changed mid-flight would append the next page to rows the
            // new window no longer covers.
            disabled={history.isFetching}
          >
            <SelectTrigger className="h-8 w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((days) => (
                <SelectItem key={days} value={String(days)}>
                  {t('pageHistory.lastDays', { count: days })}
                </SelectItem>
              ))}
              <SelectItem value="all">{t('pageHistory.retained')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {history.isError && (
        <p className="text-destructive py-8 text-center text-sm">
          {t('pageHistory.failed')}
        </p>
      )}

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-6 w-full" />
          ))}
        </div>
      )}

      {empty && (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <p className="text-muted-foreground py-8 text-center text-sm">
            {domain ? t('pageHistory.noneForApp') : t('pageHistory.none')}
          </p>
        </div>
      )}

      {days.length > 0 && (
        // Kept rather than replaced while a new range loads: the rows on
        // screen are still readable. `relative` sits outside the scroll box so
        // the spinner stays in view however far the list is scrolled.
        <div className="relative">
          <BusyOverlay show={busy} />
          <div
            aria-busy={busy}
            className={cn(
              'max-h-[28rem] overflow-y-auto pr-1',
              busy && 'opacity-60 transition-opacity'
            )}
          >
            {days.map((day) => (
              <section key={day.key}>
                <DayHeading date={day.date} />
                <ul className="divide-border/60 divide-y">
                  {day.visits.map((visit, index) => (
                    <VisitRow
                      key={`${visit.visitedOn}-${visit.page}-${index}`}
                      visit={visit}
                      showDomain={domain === null}
                    />
                  ))}
                </ul>
              </section>
            ))}

            {history.hasNextPage && (
              <div className="pt-3 text-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void history.fetchNextPage()}
                  // Any fetch, not just this one: while a new range is loading
                  // the rows on screen belong to the old one, and so would the
                  // offset the next page is asked for.
                  disabled={history.isFetching}
                >
                  {history.isFetchingNextPage && (
                    <Loader2 className="animate-spin" />
                  )}
                  {t('pageHistory.loadMore')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
